<template>
  <div class="money-transfers-page features-money-transfers-wrapper">
    <Head>
      <Title>{{ formattedMetaTitle }}</Title>
      <Meta name="description" hid="description" :content="pageDescription" />
    </Head>
    <BlocksServiceHeroNew
      service="MobileTopUps"
      :hero="cmsData.hero[0]"
      :country-slug="countrySlug"
      :breadcrumbs="breadcrumbs"
      @scroll-to-checkout="scrollToMiniCheckout"
    />
    <BlocksContentLayout
      v-if="cmsData.content"
      :content="cmsData.content"
    />
  </div>
</template>
<script setup>
import { reactive, computed } from 'vue'
import { applicationDataStore } from '@/store/application/data'
import { CMS_SERVICE_HERO_QUERY, DYNAMIC_CONTENT_QUERY, PAGE_META } from '@/utils/graphql-queries'
import request from '@/api/dato/datohelper'
const route = useRoute()
const { setLocale } = useI18n()
const { $messages, $bus, $mRouter, $track } = useNuxtApp()
const currentLocale = useLocale() || 'en'
const countrySlug = route.params.country
const dataStore = applicationDataStore()

definePageMeta({
  layout: 'default',
  stopDefaultSwitch: true,
  ref: 'mtu'
})

const data = reactive({
  country: null,
  mobileTopUpsServicePage: null,
  defaultMobileTopUpsServicePage: null,
  unsupportedMobileTopUpsServicePage: null
})

const countryMtuSlugs = {
  CU: {
    en: 'recargas',
    es: 'recargas'
  },
  NG: {
    en: 'airtime',
    es: 'recargas'
  },
  MX: {
    en: 'recargas',
    es: 'recargas'
  },
  GH: {
    en: 'airtime',
    es: 'recargas'
  },
  KE: {
    en: 'airtime',
    es: 'recargas'
  },
  ET: {
    en: 'airtime',
    es: 'recargas'
  },
  CM: {
    en: 'airtime',
    es: 'recargas'
  }
}

const alternativeMtuSlugs = {
  en: 'mobile-top-ups',
  es: 'recargas-moviles'
}

const acceptedSlugs = ['mobile-top-ups', 'airtime', 'recargas', 'recargas-moviles']

const mtuSlug = route.params.mtu

if (!acceptedSlugs.includes(mtuSlug)) {
  throw createError({ statusCode: 404, statusMessage: 'Page Not Found' })
}

data.country = dataStore.getCountryBySlug(countrySlug)

if (!data.country) {
  throw createError({ statusCode: 404, statusMessage: 'Page Not Found' })
}

const query = `query MyQuery {
      mobileTopUpsServicePage(locale: ${currentLocale}, filter: {country: {eq: "${data.country.id}"}}) {
        ${CMS_SERVICE_HERO_QUERY}
        ${DYNAMIC_CONTENT_QUERY}
        ${PAGE_META}
      }
      defaultMobileTopUpsServicePage(locale: ${currentLocale}) {
        ${CMS_SERVICE_HERO_QUERY}
        ${DYNAMIC_CONTENT_QUERY}
        ${PAGE_META}
      }
      unsupportedMobileTopUpsServicePage(locale: ${currentLocale}) {
        ${CMS_SERVICE_HERO_QUERY}
        ${DYNAMIC_CONTENT_QUERY}
        ${PAGE_META}
      }
    }`

const response = await request(query, 'MobileTopUpsDynamic' + data.country.slug + currentLocale)

if (!response || (!response.mobileTopUpsServicePage && !response.defaultMobileTopUpsServicePage && !response.unsupportedMobileTopUpsServicePage)) {
  throw createError({ statusCode: 404, statusMessage: 'Page Not Found' })
}

data.mobileTopUpsServicePage = response.mobileTopUpsServicePage
data.defaultMobileTopUpsServicePage = response.defaultMobileTopUpsServicePage
data.unsupportedMobileTopUpsServicePage = response.unsupportedMobileTopUpsServicePage

const cmsData = computed(() => {
  if (data.mobileTopUpsServicePage) {
    // if the country has a bespoke MTU Service Page Hero, use it
    return data.mobileTopUpsServicePage
  }
  if ((data.country.hasMtu) && data.defaultMobileTopUpsServicePage) {
    // if the country supports MTU but doesn't have a bespoke page, use the default hero
    const parsed = parseContentCountryName(data.defaultMobileTopUpsServicePage)
    return parsed
  }
  if ((!data.country.hasMtu) && data.unsupportedMobileTopUpsServicePage) {
    // if the country doesn't support MTU, use unsupported hero
    const parsed = parseContentCountryName(data.unsupportedMobileTopUpsServicePage)
    return parsed
  }
  // hopefully the below will never happen, it's only here because of computed functions needing a return statement
  return null
})

const countryId = computed(() => {
  return (data.country && data.country.id) || ''
})

const countryName = computed(() => {
  return (data.country && data.country.name) || ''
})

const pageName = computed(() => {
  return formattedMetaTitle.value
})

const pageDescription = computed(() => {
  return cmsData.value.pageMeta[0].pageDescription
})

const breadcrumbs = computed(() => {
  return [
    {
      title: $messages('label.home'),
      to: $mRouter.getLocalRoute({ name: 'index' })
    },
    {
      title: $messages('page_labels.mobile_top_ups'),
      to: $mRouter.getLocalRoute({ name: 'mobile-top-ups' })
    },
    {
      title: $messages(`countries.${countryId.value}`)
    }
  ]
})

const formattedMetaTitle = computed(() => {
  const meta = cmsData.value.pageMeta[0]
  if (meta) {
    let title = meta.pageTitle
    title = title.indexOf('{country}') ? insertCountryName(title) : title
    return title
  }
  return null
})

const localePath = computed(() => {
  if (data.country.hasMtu) {
    return 'dynamic.generic'
  } else {
    return 'dynamic'
  }
})

const handleLocaleSwitchHere = (val) => {
  const ISO = data.country.id
  const mtuSlug = countryMtuSlugs[ISO] ? countryMtuSlugs[ISO][val.destinationLocale] : alternativeMtuSlugs[val.destinationLocale]
  const newcountry = dataStore.getCountryBySlug(val.currentRoute.params.country)
  const countrySlug = newcountry.alternativeSlugs[val.destinationLocale]
  setLocale(val.destinationLocale).then(() => {
    if (countrySlug) {
      const newroute = $mRouter.getLocalRoute({ name: 'country-mtu', params: { country: countrySlug, mtu: mtuSlug } })
      window.location.href = newroute.href
    } else {
      window.location.href = $mRouter.getLocalRoute({ name: 'mobile-top-ups' }).href
    }
  })
}

$bus.onDynamicRouteChangeLanguage(route.meta.key, handleLocaleSwitchHere)

const scrollToMiniCheckout = () => {
  const miniCheckout = document.querySelector('#mini-checkout-form')
  if (miniCheckout) {
    miniCheckout.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    })
  }
}

const insertCountryName = (text) => {
  if (data.countryName) {
    return text.replace(/{{country}}/g, data.countryName)
  }
  return text.replace(/{{country}}/g, countrySlug)
}

const parseContentCountryName = (page) => {
  const parsedContent = []
  if (page.content) {
    for (let i = 0; i < page.content.length; i++) {
      const block = page.content[i]
      if (block.title) {
        block.title = block.title.replace(/{{country}}/g, data.country.name)
      }
      if (block.body) {
        block.body = block.body.replace(/{{country}}/g, data.country.name)
      }
      parsedContent.push(block)
    }
  }
  page.content = parsedContent
  return page
}

onMounted(() => {
  $track.pageView(route, {
    name: pageName,
    component: `/${route.params.country}/${route.params.mtu}/`
  })
})

const onCtaHeroClick = (cta) => {
  scrollToMiniCheckout()
  $track.linkClicked('MobileTopUpsHeroButton', 'cta', { linkText: cta })
}
</script>
